import { PedagogyRoom, ReactIcons, UserOnlineData } from "@kalyzee/escaet-ui";
import {
  MeetingContainer,
  MeetingContainerAPI,
  useMeetingSosOnChange,
  useMeetingUserBadBehaviorsOnChange,
  useMeetingUserInWaitingRoomOnChange,
} from "@kalyzee/kast-web-sdk";
import { Meeting, MeetingCallState, MeetingRoom, MeetingRoomStatus, MeetingSession, MeetingUserDataCallStatus, User } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo } from "react";
import { toast, ToastOptions } from "react-toastify";
import { userToEscaetUser } from "../helpers/meeting";
import { pedagogyExtraUserDataFromString } from "../helpers/pedagogy";
import { MeetingPedagogyUserData } from "../interfaces/pedagogy";
import { PedagogyContextState } from "../pages/master/pedagogy/context";
import { useUser } from "../store/session/hooks";
import { useMultiMeetingAPI, useMultiMeetingSelector } from "./meeting";
import { playNotificationSoundEffect, playSosSoundEffect } from "../helpers/sounds";

/**
 * Create peda users for every online users in the meetings.
 * The late users can have late informations.
 */
export const usePedagogyUsers = () => {
  const multiMeetingAPI = useMultiMeetingAPI();
  const meetingContainersAPI = useMemo(() => multiMeetingAPI.all?.getContainers()?.map((c) => c.getAPI()) ?? [], [multiMeetingAPI]);
  const [allMeetings] = useMultiMeetingSelector(meetingContainersAPI, (state) => state.meeting);
  const [allRooms] = useMultiMeetingSelector(meetingContainersAPI, (state) => state.rooms);

  const { meetings, pedaMeetings } = useMemo(() => {
    const meetings: Meeting[] = [];
    const pedaMeetings: { meeting: Meeting; rooms?: MeetingRoom[] }[] = [];
    allMeetings.forEach((meeting) => {
      if (!meeting) return;
      if (meeting.specializedData?.isPedagogy) {
        const rooms = allRooms.flat().filter((r) => r && r?.meetingId === meeting.id) as MeetingRoom[];
        pedaMeetings.push({ meeting: meeting, rooms: rooms });
      } else {
        meetings.push(meeting);
      }
    });
    return { meetings, pedaMeetings };
  }, [allMeetings, allRooms]);

  const selfUser = useUser();

  /*
  const onlinePedaUsersIds = useMemo(() => {
    const onlineUserIds = new Set<string>();
    pedaMeetings.forEach(({ meeting }) => {
      meeting.sessions?.forEach((session) => {
        const user = session.user;
        if (!user) return;
        if (user.id === selfUser?.id) return;
        if (isMeetingSessionRoleLessThan(session.role, MeetingSessionRole.ADMINISTRATOR)) return;
        onlineUserIds.add(user.id);
      });
    });
    return onlineUserIds;
  }, [pedaMeetings, selfUser]);*/

  const pedaUsers = useMemo(() => {
    const users: MeetingPedagogyUserData[] = [];
    meetings.forEach((meeting) => {
      const newUsersAreLate = 
        meeting.callEnabled 
        && (!meeting.callState || meeting.callState === MeetingCallState.CLOSED) 
        && (meeting.callWasDone || meeting.endCallWasDone);

      const onlineUsers = new Map<string, { user: User; sessions: MeetingSession[] }>();

      // We collect the online users
      meeting.sessions?.forEach((session) => {
        const user = session.user;
        if (!user) return;
        if (user.id === selfUser?.id) return;
        const userWithSessions = onlineUsers.get(user.id);
        if (userWithSessions) {
          userWithSessions.sessions.push(session);
        } else {
          onlineUsers.set(user.id, { user, sessions: [session] });
        }
      });

      meeting.usersData?.forEach((userData) => {
        const user = onlineUsers.get(userData.userId);
        if (!user) return; // not an online user

        const extraData = pedagogyExtraUserDataFromString(userData?.call?.extraData);

        let notAllowedIn: boolean = false;
        let userMessage: string | undefined;
        let userLateSince: Date | undefined;
        let kickedByExpert: boolean = false;

        userMessage = userData.call?.userMessage;

        if (newUsersAreLate && userData.call?.status !== MeetingUserDataCallStatus.ONLINE && userData.call?.status !== MeetingUserDataCallStatus.ONSITE) {
          notAllowedIn = true;
          if (extraData.late) {
            // The user is late
            // Filling late data
            userLateSince = meeting.startedAt ? new Date(meeting.startedAt) : undefined;
          }
        }

        if (userData.call?.status === MeetingUserDataCallStatus.KICKED) {
          notAllowedIn = true;
          kickedByExpert = true;
        }

        // a student is considered with pedagogy when :
        // - he is enlisted in a pedagogy room
        // - the pedagogy room is not closed
        const withPedagogy = pedaMeetings.some((pedaMeeting) =>
          pedaMeeting.rooms?.some((room) => room.status !== MeetingRoomStatus.Closed && room.enlistedUserIds?.includes(userData.userId))
        );

        const pedaUser: MeetingPedagogyUserData = {
          ...userToEscaetUser(user.user),
          message: userMessage,
          lateSince: userLateSince,
          roomId: meeting.room?.id,
          meetingId: meeting.id,
          sessions: user.sessions,
          kickedByExpert,
          withPedagogy,
          inWaitingRoom: notAllowedIn && !!userMessage,
        };

        if (!pedaUser.inWaitingRoom) {
          // we only add behavior data if user is not in waiting room
          // otherwise he might end up in both waiting room and behavior room

          const badBehaviors: UserOnlineData["badBehaviors"] = {
            hasNoCamera: false,
            hasNoMicrophone: false, // not asked for now
            hasBadNetwork: false,
            isNotAttentive: false,
          };

          // If user has many sessions, only one of them is enough to determine a bad behavior
          user.sessions.forEach((session) => {
            const alerts = session.alerts?.entries;
            if (!alerts) return;
            if (alerts.hasNoCamera?.level && !alerts.hasNoCamera.disabled) {
              badBehaviors.hasNoCamera = true;
            }
            if (alerts.hasBadNetwork?.level && !alerts.hasBadNetwork.disabled) {
              badBehaviors.hasBadNetwork = true;
            }
            if (alerts.isNotAttentive?.level && !alerts.isNotAttentive.disabled) {
              badBehaviors.isNotAttentive = true;
            }
          });

          pedaUser.badBehaviors = badBehaviors;
        }

        users.push(pedaUser);
      });
    });

    return users;
  }, [meetings, pedaMeetings, selfUser]);

  return pedaUsers;
};

export const usePedagogyRooms = (rooms: { id: string; name: string }[]) =>
  useMemo(() => {
    const rooms: PedagogyRoom[] = [];

    rooms.forEach((room) => {
      const pedaRoom = {
        id: room.id,
        name: room.name ?? `Salle ${room.id.substring(0, 3)}`,
      };
      rooms.push(pedaRoom);
    });

    return rooms;
  }, [rooms]);

export const usePedagogyContainer = (context: PedagogyContextState | undefined) => {
  return useMemo(() => (context?.pedagogyContainers?.length ? context.pedagogyContainers[0] : undefined), [context?.pedagogyContainers]);
};

export const usePedagogyContainerAPI = (context: PedagogyContextState | MeetingContainer | undefined) => {
  return useMemo(() => {
    if (!context) return undefined;
    if (context instanceof MeetingContainer) return context.getAPI();
    const container = context?.pedagogyContainers?.length ? context.pedagogyContainers[0] : undefined;
    return container?.getAPI();
  }, [context]);
};

export const usePedagogyNotifications = (containers: MeetingContainerAPI | MeetingContainerAPI[] | undefined, sound = true) => {
  useMeetingSosOnChange(containers, (sos) => {
    const trigger = sos === true;
    if (trigger) {
      const icon = <ReactIcons.Headphone stroke="white" fill="white" width="20" height="20" />;
      const toastOptions: ToastOptions = {
        className: "toast",
        style: { backgroundColor: "#f05e5e", color: "white", fontFamily: "Nunito, sans-serif", fontWeight: 600 },
        progressStyle: { background: "#ff7f7f" },
        icon,
        pauseOnHover: true,
        autoClose: 8000
      };
      playSosSoundEffect()
      toast("Un SOS a été déclenché", toastOptions);
    }
  });
  useMeetingUserBadBehaviorsOnChange(containers, (data, container) => {
    const { lastBadBehaviors, newBadBehaviors } = data;
    const trigger = Object.values(lastBadBehaviors).every((e) => e === false) && Object.values(newBadBehaviors).some((e) => e === true);
    if (trigger) {
      const user = container.store.getState().meeting?.sessions?.find((s) => s.id === data.sessionId)?.user;
      let username = user ? `${user.username}` : undefined;
      const icon = <ReactIcons.PedagoLogoComportementWhite height="20" />;
      const toastOptions: ToastOptions = {
        className: "toast",
        style: { backgroundColor: "#ffa850", color: "white", fontFamily: "Nunito, sans-serif", fontWeight: 600 },
        progressStyle: { background: "#ffc080" },
        icon,
        pauseOnHover: true,
        autoClose: 8000
      };
      playNotificationSoundEffect();
      if (username) toast(`Nouveau mauvais comportement détecté pour ${username}`, toastOptions);
      else toast("Nouveau mauvais comportement détecté", toastOptions);
    }
  });
  useMeetingUserInWaitingRoomOnChange(containers, (data, container) => {
    const trigger = data.inWaitingRoom === true;
    if (trigger) {
      const user = container.store.getState().meeting?.sessions?.find((s) => s.user?.id === data.userId)?.user;
      let username = user ? `${user.username}` : undefined;
      const icon = <ReactIcons.PedagoLogoDoorWhite height="20" />;
      const toastOptions: ToastOptions = {
        className: "toast",
        style: { backgroundColor: "#f05e5e", color: "white", fontFamily: "Nunito, sans-serif", fontWeight: 600 },
        progressStyle: { background: "#ff7f7f" },
        icon,
        pauseOnHover: true,
        autoClose: 8000
      };
      playNotificationSoundEffect();
      if (username) toast(`${username} vient d'arriver en salle d'attente`, toastOptions);
      else toast("Une nouvelle personne vient d'arriver en salle d'attente", toastOptions);
    }
  });
};
